import { Messages } from '../../codegen/Localize';
import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { IActionContext } from '../helpers/ActionContext';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerFilterClauses } from '../helpers/ServerFilterClauses';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { L10n } from '../L10n/L10n';
import { FlowMapper } from '../mappers/FlowMapper';
import { FlowBaseInfo, FlowDetailInfo, FlowListItemInfo } from '../models/FlowInfo';
import { FlowOutputStepInfo } from '../models/FlowOutputStepInfo';
import { ITaggableContent } from '../models/ITaggableContent';
import { Module } from '../Module';
import { AddTagsToFlowsRequest } from '../ServerApi/requests/AddTagsToFlowsRequest';
import { CreateRunFlowTaskRequest } from '../ServerApi/requests/CreateRunFlowTaskRequest';
import { DeleteFlowsRequest } from '../ServerApi/requests/DeleteFlowsRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetFlowActionsRequest } from '../ServerApi/requests/GetFlowActionsRequest';
import { GetFlowInputStepsRequest } from '../ServerApi/requests/GetFlowInputStepsRequest';
import { GetFlowOutputStepsRequest } from '../ServerApi/requests/GetFlowOutputStepsRequest';
import { GetFlowsRequest } from '../ServerApi/requests/GetFlowsRequest';
import { MoveFlowsToProjectRequest } from '../ServerApi/requests/MoveFlowsToProjectRequest';
import { PerformExtractEncryptionActionOnFlowsRequest } from '../ServerApi/requests/PerformExtractEncryptionActionOnFlowsRequest';
import { RemoveTagsFromFlowsRequest } from '../ServerApi/requests/RemoveTagsFromFlowsRequest';
import { RevertFlowRequest, RevertFlowResult } from '../ServerApi/requests/RevertFlowRequest';
import { RunFlowOnOutputStepsRequest } from '../ServerApi/requests/RunFlowsOnOutputStepsRequest';
import { RunFlowsRequest } from '../ServerApi/requests/RunFlowsRequest';
import { SetFlowDescriptionRequest } from '../ServerApi/requests/SetFlowDescriptionRequest';
import { SetFlowNameRequest } from '../ServerApi/requests/SetFlowNameRequest';
import { SetFlowsOwnerRequest } from '../ServerApi/requests/SetFlowsOwnerRequest';
import { FlowAllowedActions } from '../ServerApi/types/FlowAllowedActions';
import { FlowInputStep } from '../ServerApi/types/FlowInputStep';
import { FlowOutputStep } from '../ServerApi/types/FlowOutputStep';
import { UnencryptedConnectionKeychain } from '../ServerApi/types/IConnectionKeyChain';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IResult } from '../ServerApi/types/IResponse';
import { ISchedule, ScheduleActions } from '../ServerApi/types/ISchedule';
import { IChangeScheduleScope } from '../services/ChangeScheduleAction';
import { ConfirmActionDialog } from '../services/ConfirmActionDialog';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { SearchSuggestionsService } from '../services/SearchSuggestionsService';
import { ServerService } from '../services/ServerService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export class Flows implements IListResource<FlowListItemInfo>, IFieldValuesResource, IDetailResource<FlowDetailInfo>, IActionResource<FlowAllowedActions>, ITaggableContent {

  public static $inject = [
    '$q',
    'ConfirmActionDialog',
    'ErrorNotifyingServer',
    'FetcherFactory',
    'scheduleFrequencyDescriptionFilter',
    'SearchSuggestionsService'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<FlowListItemInfo>;
  private allQuery: ResourceQuery<FlowListItemInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;
  private detailFetcher: IDetailFetcher<FlowDetailInfo>;

  constructor(
    private $q: ng.IQService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory,
    private scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    private SearchSuggestionsService: SearchSuggestionsService,
  ) {
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetFlowsRequest, ServerApiMapper.flows);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getFlow', FlowMapper.mapDetail);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
    this.server = ErrorNotifyingServer.getServerService();
  }

  public all(): ResourceQuery<FlowListItemInfo> {
    return this.allQuery;
  }

  public actions(id: string): ng.IPromise<FlowAllowedActions> {
    return this.server.sendRequest(new GetFlowActionsRequest(id));
  }

  public addTags(ids: string[], tags: string[]): ng.IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : this.$q.when({});
  }

  public removeTags(ids: string[], tags: string[]): ng.IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromFlowsRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : this.$q.when({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): ng.IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd));
  }

  public moveToProject(targetProjectId: string, flowIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(flowIds, (idsBlock) => new MoveFlowsToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public getById(id: string): ng.IPromise<FlowDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'flow');
  }

  public getFlowsByDatasourceId(datasourceId: string): ng.IPromise<FlowListItemInfo[]>{
    const query =  this.all().addFilter(ServerFilterClauses.serverFilterClauses.datasource(datasourceId));
    return SliceableHelper.fetchAll(query).then(listResult => listResult.items);
  }

  public getInputSteps(flowId: string): ng.IPromise<ReadonlyArray<FlowInputStep>> {
    return this.server.sendRequest(new GetFlowInputStepsRequest({ flowId }))
      .then((result) => result.inputSteps || []); // what to do in the case of an error?
  }

  public getOutputSteps(flowId: string): ng.IPromise<ReadonlyArray<FlowOutputStep>> {
    return this.server.sendRequest(new GetFlowOutputStepsRequest({ flowId }))
      .then((result) => result.outputSteps || []); // what to do in the case of an error?
  }

  public getOutputStepInfos(flowId: string): ng.IPromise<ReadonlyArray<FlowOutputStepInfo>> {
    return this.server.sendRequest(new GetFlowOutputStepsRequest({ flowId}))
      .then((result) => ServerApiMapper.flowOutputSteps(result));
  }

  public rename(id: string, name: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new SetFlowNameRequest({id: id, name: name}));
  }

  public runFlow(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunFlowsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public runFlowOnOutputSteps(flowId: string, flowOutputStepIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(
      new RunFlowOnOutputStepsRequest({
        flowId: flowId,
        flowOutputStepIds: flowOutputStepIds
      }));
  }

  public setDescription(id: string, description: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequest(new SetFlowDescriptionRequest(id, description));
  }

  public setOwner(ids: string[], ownerId: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetFlowsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public createRunFlowTask(id: string, scheduleId: string, runAllOutputs: boolean, flowOutputStepIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(
      new CreateRunFlowTaskRequest({
        scheduleId: scheduleId,
        flowId: id,
        runAllOutputs: runAllOutputs,
        flowOutputStepIds: flowOutputStepIds,
      }));
  }

  public createRunFlowTaskForSingleOutput(
    flow: FlowBaseInfo,
    flowOutputStepId: string,
    context: IActionContext,
    onSuccess?: () => void
  ): ng.IPromise<IResult> {

    const scope: IChangeScheduleScope = <IChangeScheduleScope> context.scope.$new();

    scope.descriptionTranslateKey = 'createScheduleForSingleFlowOutputPrompt';
    scope.descriptionTranslateValues = { FLOW_NAME: L10n.elideIfLong(flow.name) };

    scope.scheduleSource = this.SearchSuggestionsService.schedulesSource(ScheduleActions.Flow);
    scope.filterList = {
      model: null
    };

    scope.itemTextFn = (schedule: ISchedule) => {
      return this.scheduleFrequencyDescriptionFilter(schedule);
    };

    scope.displayScheduleDescriptionAsName = this.server.displayScheduleDescriptionAsName();

    const getSchedule = () => scope.filterList.model;

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeSchedule.html',
      title: Messages.newTask(),
      actionName: Messages.scheduleFlowRun(),
      action: () => { return this.createRunFlowTask(flow.id, getSchedule().id, false, [flowOutputStepId]).then(() => onSuccess && onSuccess()); },
      confirmDisabled: () => !getSchedule()
    });

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  public revertFlow(id: string, version: number, keychains?: UnencryptedConnectionKeychain[], encryptedPasswordKeyId?: string): ng.IPromise<RevertFlowResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new RevertFlowRequest({
      id,
      version,
      keychains,
      encryptedPasswordKeyId
    }));
  }

  public deleteFlows(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteFlowsRequest(idsBlock)),
      ResultHelper.combineResults
    );
  }

  public encryptFlows(ids: string[], actionType: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnFlowsRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  public static hasDataQualityWarning(contentItem: FlowBaseInfo): boolean {
    return contentItem.dataQualityIndicators
        && contentItem.dataQualityIndicators[0]
        && contentItem.dataQualityIndicators[0].isActive;
  }
}

Module.instance().service('Flows', Flows);
